import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, CreditCard, CheckCircle, Phone, Sparkles } from 'lucide-react';
import { useBooking } from '../hooks/useBooking';
import { useToast } from '../hooks/useToast';
import { services } from '../data/services';
import { formatPrice } from '../utils/helpers';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import ProgressSteps from '../components/ui/ProgressSteps';
import CalendarComponent from '../components/ui/Calendar';
import Badge from '../components/ui/Badge';

const WHATSAPP_NUMBERS = {
  'craniosacral-therapy': '919448602522',
  'meditation': '918904568399',
  'personal': '919448602522'
};
const DEFAULT_WHATSAPP_NUMBER = '918904568399';

const BookService = () => {
  const booking = useBooking();
  const toast = useToast();
  const location = useLocation();
  const [detailErrors, setDetailErrors] = useState({});

  const clearDetailError = (field) => {
    setDetailErrors((prev) => {
      if (!prev[field]) return prev;
      const nextErrors = { ...prev };
      delete nextErrors[field];
      return nextErrors;
    });
  };

  const formatBookingDate = (date) => {
    if (!date) return 'Not selected';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const buildWhatsAppMessage = () => {
    const service = booking.selectedService;
    const customerDetails = booking.customerDetails;
    const formattedDate = formatBookingDate(booking.selectedDate);

    const lines = [
      'Hello! I would like to book a session:',
      '',
      `*Service:* ${service?.title || 'Not selected'}`,
      `*Date:* ${formattedDate}`,
      `*Time:* ${booking.selectedTime || 'Not selected'}`,
      `*Name:* ${customerDetails.name}`,
      `*Phone:* ${customerDetails.phone}`,
      `*Email:* ${customerDetails.email}`
    ];

    if (customerDetails.notes) {
      lines.push(`*Requirements/Notes:* ${customerDetails.notes}`);
    }

    lines.push('', 'Please confirm my booking. Thank you!');

    return lines.join('\n');
  };

  const openWhatsApp = () => {
    const whatsappNumber = WHATSAPP_NUMBERS[booking.selectedService?.id] || DEFAULT_WHATSAPP_NUMBER;
    const encodedMessage = encodeURIComponent(buildWhatsAppMessage());
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    document.title = 'Book a Service - Pragyavan Wellness Centre';
    
    // Check if a service was selected from the home page
    const selectedServiceId = location.state?.selectedService;
    if (selectedServiceId && !booking.selectedService && booking.currentStep === 'service') {
      // Auto-select the service and skip to datetime step
      booking.selectService(selectedServiceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bookingSteps = [
    { id: 'service', label: 'Select Service', description: 'Choose your wellness service' },
    { id: 'datetime', label: 'Date & Time', description: 'Pick your preferred slot' },
    { id: 'details', label: 'Your Details', description: 'Personal information' },
    { id: 'payment', label: 'Payment', description: 'Secure checkout' },
    { id: 'confirmation', label: 'Confirmation', description: 'Booking confirmed' }
  ];

  const handleServiceSelect = (serviceId) => {
    setDetailErrors({});
    booking.selectService(serviceId);
  };

  const handleDateTimeSelect = () => {
    if (!booking.selectedDate || !booking.selectedTime) {
      toast.showError('Please select date and time', 'Both date and time are required to proceed.');
      return;
    }
    setDetailErrors({});
    booking.goToStep('details');
  };

  const handleDetailsSubmit = () => {
    const errors = booking.getValidationErrors();
    setDetailErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.showError('Please fill required fields', 'Name, email, and phone are required.');
      return;
    }
    openWhatsApp();
    toast.showSuccess('Opening WhatsApp...', 'Please send the message to confirm your booking.');
  };

  const handlePayment = async () => {
    try {
      await booking.processPayment();
      toast.showSuccess('Payment Successful!', 'Your booking has been confirmed. Check your email for details.');
    } catch (error) {
      toast.showError('Payment Failed', error.message);
    }
  };

  const renderServiceSelection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-brown text-xs font-medium mb-4">
          <Sparkles className="w-3 h-3" />
          <span>Start Your Journey</span>
        </div>
        <h1 className="text-4xl font-display font-bold text-pragyavan-brown mb-4">Select Your Service</h1>
        <p className="text-warm-gray text-lg max-w-2xl mx-auto">Choose the wellness service that resonates with your current needs and aspirations.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        {services.map((service) => (
          <Card
            key={service.id}
            className="cursor-pointer transition-all duration-300 hover:shadow-premium hover:-translate-y-1 group relative overflow-hidden border-0 shadow-glass bg-white/80 backdrop-blur-sm"
            onClick={() => handleServiceSelect(service.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleServiceSelect(service.id);
              }
            }}
            padding="none"
          >
            {/* Decorative gradient */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-pragyavan-gold/50 to-sage-green/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-display font-semibold text-pragyavan-brown group-hover:text-pragyavan-gold transition-colors duration-300">{service.title}</h3>
                {service.badge && (
                  <Badge variant={service.badge === 'Popular' ? 'success' : 'warning'} className="shadow-sm">
                    {service.badge}
                  </Badge>
                )}
              </div>

              <p className="text-warm-gray text-sm mb-6 line-clamp-2 leading-relaxed">{service.description}</p>

              <div className="flex justify-between items-center mb-6 pb-6 border-b border-warm-gray/10">
                <span className="text-2xl font-bold text-pragyavan-brown">{formatPrice(service.price)}</span>
                <span className="text-warm-gray text-sm flex items-center bg-warm-gray/5 px-3 py-1 rounded-full">
                  <Clock className="w-4 h-4 mr-1.5" />
                  {service.duration}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                {service.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-center text-sm text-warm-gray">
                    <CheckCircle className="w-4 h-4 text-sage-green mr-3 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="w-full group-hover:bg-pragyavan-brown group-hover:text-white group-hover:border-pragyavan-brown transition-all duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleServiceSelect(service.id);
                }}
              >
                Select This Service
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </motion.div>
  );

  const renderDateTimeSelection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-pragyavan-brown mb-2">Choose Date & Time</h2>
        <p className="text-warm-gray">Select your preferred appointment slot for <span className="font-semibold text-pragyavan-gold">{booking.selectedService?.title}</span></p>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Calendar */}
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-glass border border-white/50">
          <h3 className="text-lg font-semibold text-pragyavan-brown mb-6 flex items-center">
            <Calendar className="w-5 h-5 mr-2 text-pragyavan-gold" />
            Select Date
          </h3>
          <CalendarComponent
            selectedDate={booking.selectedDate}
            onDateSelect={(date) => booking.selectDateTime(date, booking.selectedTime)}
          />
        </div>

        {/* Time Slots */}
        <div className="bg-white/60 backdrop-blur-sm p-6 rounded-2xl shadow-glass border border-white/50">
          <h3 className="text-lg font-semibold text-pragyavan-brown mb-6 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-pragyavan-gold" />
            Available Times
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {booking.selectedService?.availableSlots.map((timeSlot) => (
              <Button
                key={timeSlot}
                variant={booking.selectedTime === timeSlot ? 'primary' : 'outline'}
                size="sm"
                onClick={() => booking.selectDateTime(booking.selectedDate, timeSlot)}
                className={`justify-center py-3 ${booking.selectedTime === timeSlot ? 'shadow-lg shadow-pragyavan-gold/20' : ''}`}
              >
                {timeSlot}
              </Button>
            ))}
          </div>

          {booking.selectedDate && booking.selectedTime && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 p-4 bg-pragyavan-gold/10 rounded-xl border border-pragyavan-gold/20"
            >
              <p className="text-sm text-pragyavan-brown font-medium flex items-center justify-center">
                <CheckCircle className="w-4 h-4 mr-2 text-pragyavan-gold" />
                Selected: {booking.selectedDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })} at {booking.selectedTime}
              </p>
            </motion.div>
          )}
        </div>
      </div>

      <div className="flex justify-between pt-8 border-t border-warm-gray/10">
        <Button variant="ghost" onClick={() => booking.goToStep('service')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleDateTimeSelect} disabled={!booking.canProceed()} className="shadow-lg shadow-pragyavan-gold/20">
          Continue to Details
        </Button>
      </div>
    </motion.div>
  );

  const renderCustomerDetails = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-8"
    >
      <div className="text-center mb-8">
        <h2 className="text-3xl font-display font-bold text-pragyavan-brown mb-2">Your Information</h2>
        <p className="text-warm-gray">Please provide your details for the booking confirmation</p>
      </div>

      <Card padding="large" className="shadow-glass border-0 bg-white/80 backdrop-blur-sm">
        <div className="grid md:grid-cols-2 gap-8">
          <Input
            label="Full Name"
            type="text"
            required
            placeholder="Enter your full name"
            value={booking.customerDetails.name}
            onChange={(e) => {
              booking.updateCustomerDetails({ name: e.target.value });
              clearDetailError('name');
            }}
            error={detailErrors.name}
          />

          <Input
            label="Email Address"
            type="email"
            required
            placeholder="your@email.com"
            value={booking.customerDetails.email}
            onChange={(e) => {
              booking.updateCustomerDetails({ email: e.target.value });
              clearDetailError('email');
            }}
            error={detailErrors.email}
          />

          <Input
            label="Phone Number"
            type="tel"
            required
            placeholder="+91 8904568399"
            value={booking.customerDetails.phone}
            onChange={(e) => {
              booking.updateCustomerDetails({ phone: e.target.value });
              clearDetailError('phone');
            }}
            error={detailErrors.phone}
          />

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-pragyavan-brown mb-2">
              Special Requirements (Optional)
            </label>
            <textarea
              className="w-full px-4 py-3 border border-warm-gray/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-pragyavan-gold/50 focus:border-pragyavan-gold transition-colors bg-white/50"
              rows={4}
              placeholder="Any specific needs or health considerations we should know about..."
              value={booking.customerDetails.notes}
              onChange={(e) => booking.updateCustomerDetails({ notes: e.target.value })}
            />
          </div>
        </div>
      </Card>

      <div className="flex justify-between pt-8 border-t border-warm-gray/10">
        <Button variant="ghost" onClick={() => booking.goToStep('datetime')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button onClick={handleDetailsSubmit} disabled={!booking.canProceed()} className="shadow-lg shadow-pragyavan-gold/20">
          Continue
        </Button>
      </div>
    </motion.div>
  );

  const renderPaymentSummary = () => {
    const summary = booking.getBookingSummary();

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold text-pragyavan-brown mb-2">Booking Summary</h2>
          <p className="text-warm-gray">Review your booking details before payment</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Booking Summary */}
          <Card className="shadow-glass border-0 bg-white/80 backdrop-blur-sm">
            <Card.Header>
              <h3 className="text-lg font-semibold text-pragyavan-brown flex items-center">
                <Sparkles className="w-4 h-4 mr-2 text-pragyavan-gold" />
                Booking Details
              </h3>
            </Card.Header>
            <Card.Body className="space-y-4">
              <div className="flex items-center justify-between py-2 border-b border-warm-gray/10">
                <span className="text-warm-gray">Service</span>
                <span className="font-medium text-pragyavan-brown">{summary.service.title}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-warm-gray/10">
                <span className="text-warm-gray">Date</span>
                <span className="font-medium text-pragyavan-brown">
                  {summary.date.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-warm-gray/10">
                <span className="text-warm-gray">Time</span>
                <span className="font-medium text-pragyavan-brown">{summary.time}</span>
              </div>

              <div className="flex items-center justify-between py-2 border-b border-warm-gray/10">
                <span className="text-warm-gray">Duration</span>
                <span className="font-medium text-pragyavan-brown">{summary.service.duration}</span>
              </div>

              <div className="flex items-center justify-between text-lg pt-4">
                <span className="font-semibold text-pragyavan-brown">Total Amount</span>
                <span className="font-bold text-2xl text-pragyavan-brown">{formatPrice(summary.total)}</span>
              </div>
            </Card.Body>
          </Card>

          {/* Customer Details */}
          <Card className="shadow-glass border-0 bg-white/80 backdrop-blur-sm">
            <Card.Header>
              <h3 className="text-lg font-semibold text-pragyavan-brown flex items-center">
                <User className="w-4 h-4 mr-2 text-pragyavan-gold" />
                Your Information
              </h3>
            </Card.Header>
            <Card.Body className="space-y-6">
              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <User className="w-5 h-5 text-warm-gray mr-4" />
                <div>
                  <p className="text-xs text-warm-gray uppercase tracking-wider">Name</p>
                  <p className="text-pragyavan-brown font-medium">{summary.customer.name}</p>
                </div>
              </div>

              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <span className="w-5 h-5 text-warm-gray mr-4 flex items-center justify-center font-bold">@</span>
                <div>
                  <p className="text-xs text-warm-gray uppercase tracking-wider">Email</p>
                  <p className="text-pragyavan-brown font-medium">{summary.customer.email}</p>
                </div>
              </div>

              <div className="flex items-center p-3 bg-white/50 rounded-xl">
                <Phone className="w-5 h-5 text-warm-gray mr-4" />
                <div>
                  <p className="text-xs text-warm-gray uppercase tracking-wider">Phone</p>
                  <p className="text-pragyavan-brown font-medium">{summary.customer.phone}</p>
                </div>
              </div>

              {summary.customer.notes && (
                <div className="pt-4 border-t border-warm-gray/10">
                  <p className="text-sm text-warm-gray mb-2">Special Requirements:</p>
                  <p className="text-sm text-pragyavan-brown bg-white/50 p-3 rounded-xl italic">"{summary.customer.notes}"</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Payment Section */}
        <Card background="cream" className="border-0 shadow-inner">
          <Card.Body className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white flex items-center justify-center shadow-sm">
              <CreditCard className="w-8 h-8 text-pragyavan-brown" />
            </div>
            <h3 className="text-xl font-semibold text-pragyavan-brown mb-2">Secure Payment</h3>
            <p className="text-warm-gray mb-8">Complete your booking securely with Razorpay</p>

            <div className="flex justify-center space-x-6 mb-8">
              <div className="flex items-center text-sm text-warm-gray bg-white/50 px-3 py-1 rounded-full">
                <CheckCircle className="w-4 h-4 text-sage-green mr-2" />
                256-bit SSL Encrypted
              </div>
              <div className="flex items-center text-sm text-warm-gray bg-white/50 px-3 py-1 rounded-full">
                <CheckCircle className="w-4 h-4 text-sage-green mr-2" />
                PCI DSS Compliant
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="flex justify-between pt-8 border-t border-warm-gray/10">
          <Button variant="ghost" onClick={() => booking.goToStep('details')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Button
            onClick={handlePayment}
            disabled={booking.isProcessing}
            loading={booking.isProcessing}
            className="min-w-[200px] shadow-lg shadow-pragyavan-gold/20"
            size="large"
          >
            {booking.isProcessing ? 'Processing Payment...' : `Pay ${formatPrice(summary.total)}`}
          </Button>
        </div>
      </motion.div>
    );
  };

  const renderConfirmation = () => {
    const summary = booking.getBookingSummary();

    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center space-y-8"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        >
          <div className="w-24 h-24 mx-auto bg-sage-green/10 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-sage-green" />
          </div>
        </motion.div>

        <div>
          <h1 className="text-4xl font-display font-bold text-pragyavan-brown mb-2">Booking Confirmed!</h1>
          <p className="text-warm-gray text-lg">Your wellness session has been successfully booked</p>
        </div>

        <Card className="text-left max-w-md mx-auto shadow-glass border-0 bg-white/80 backdrop-blur-sm">
          <Card.Header className="border-b border-warm-gray/10 pb-4">
            <h3 className="text-lg font-semibold text-pragyavan-brown text-center">Booking Details</h3>
          </Card.Header>
          <Card.Body className="space-y-4 pt-4">
            <div className="flex justify-between">
              <span className="text-warm-gray">Service</span>
              <span className="font-medium text-pragyavan-brown">{summary.service.title}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-warm-gray">Date</span>
              <span className="font-medium text-pragyavan-brown">
                {summary.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-warm-gray">Time</span>
              <span className="font-medium text-pragyavan-brown">{summary.time}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-warm-gray/10">
              <span className="text-warm-gray">Booking ID</span>
              <span className="font-mono text-sm text-pragyavan-brown bg-warm-gray/10 px-2 py-0.5 rounded">
                {summary.payment?.transactionId || 'Pending'}
              </span>
            </div>
          </Card.Body>
        </Card>

        <Card background="cream" className="max-w-2xl mx-auto border-0">
          <Card.Body>
            <h4 className="font-semibold text-pragyavan-brown mb-4 text-lg">What's Next?</h4>
            <div className="grid md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span className="text-left">Confirmation email sent to {summary.customer.email}</span>
              </div>
              <div className="flex items-start p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span className="text-left">Calendar invite included with location details</span>
              </div>
              <div className="flex items-start p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span className="text-left">Arrive 10 minutes early for check-in</span>
              </div>
              <div className="flex items-start p-3 bg-white/50 rounded-xl">
                <CheckCircle className="w-5 h-5 text-sage-green mr-3 flex-shrink-0" />
                <span className="text-left">Bring comfortable clothing and an open mind</span>
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="flex justify-center space-x-4 pt-4">
          <Button variant="outline" onClick={booking.resetBooking}>
            Book Another Session
          </Button>
          <Button onClick={() => window.location.href = '/'}>
            Return Home
          </Button>
        </div>
      </motion.div>
    );
  };

  const renderCurrentStep = () => {
    switch (booking.currentStep) {
      case 'service':
        return renderServiceSelection();
      case 'datetime':
        return renderDateTimeSelection();
      case 'details':
        return renderCustomerDetails();
      case 'payment':
        return renderPaymentSummary();
      case 'confirmation':
        return renderConfirmation();
      default:
        return renderServiceSelection();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="container mx-auto px-4 pt-24 pb-12"
    >
      {/* Progress Steps - Hide on confirmation */}
      {booking.currentStep !== 'confirmation' && (
        <div className="mb-16">
          <ProgressSteps
            steps={bookingSteps}
            currentStep={booking.currentStep}
            className="max-w-4xl mx-auto"
          />
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto">
        {renderCurrentStep()}
      </div>
    </motion.div>
  );
};

export default BookService;
